<template>
    <div
        ref="sidebar"
        class="z-50 fixed inset-0 w-screen lg:grow-0 lg:shrink-0 lg:basis-120 lg:static lg:w-auto print:hidden"
        :class="{ 'sidebar-force-open': forceOpen, 'sidebar-force-close': !forceOpen, 'sidebar-loading': !isLoaded }"
    >
        <img
            src="/images/logos/access_navigation.svg"
            alt="KaDeWe Access"
            class="h-7.5 w-auto hidden lg:block"
        />
        <aside
            class="w-full h-full bg-black text-white flex flex-col grow justify-between overflow-auto max-h-screen lg:max-h-none"
            :class="{
                'bg-christmas!': user?.special_events?.christmas,
            }"
        >
            <div class="p-7 pt-5 pb-3 lg:p-20 lg:pb-10">
                <div class="sticky top-0 lg:top-5 flex flex-col justify-between min-h-full">
                    <div>
                        <div class="-mx-7 -mt-4 lg:hidden max-w-full">
                            <account-sidebar-toggle :white="true" />
                        </div>

                        <img
                            src="/images/logos/access_navigation.svg"
                            alt="KaDeWe Access"
                            class="h-7.5 w-auto -mx-3.5 mb-8 block lg:hidden"
                        />

                        <p
                            class="uppercase style-title-1 mt-6 mb-4"
                            :class="sidebarTextColorClass"
                        >
                            {{ sidebarStatusLocalized }}

                            <layout-tooltip
                                v-if="!user?.foth.is_foth"
                                position="right"
                                position-alternate="bottom"
                                class="text-white"
                            >
                                <template #tooltip>
                                    <p
                                        class="mb-4 text-sm"
                                        v-text="$t('sidebar.valid_to_tooltip.headline', { date: cardValidUntil })"
                                    />
                                    <p
                                        class="text-sm"
                                        v-text="$t('sidebar.valid_to_tooltip.description')"
                                    />
                                </template>

                                <template #icon>
                                    <icon-info-black class="w-5 h-5 block" />
                                </template>

                                <template #default>
                                    <icon-info class="w-5 h-5 p-1 inline-block relative -top-2 -left-1" />
                                </template>
                            </layout-tooltip>
                        </p>
                        <h3
                            class="text-left my-0 style-h5 text-white"
                            :class="{
                                'text-color-superior': user?.loyalty_status === 'superior',
                                'text-color-royal': user?.loyalty_status === 'royal',
                            }"
                        >
                            {{ $t("sidebar.welcome") }}<br />
                            {{ user?.name ?? "Unbekannt" }}
                        </h3>

                        <p
                            class="style-label-2 mt-2 text-white"
                            :class="{
                                'text-color-superior': user?.loyalty_status === 'superior',
                                'text-color-royal': user?.loyalty_status === 'royal',
                            }"
                            v-text="$t('sidebar.member_since', { date: memberSince })"
                        />

                        <nav class="mt-8 style-h6 font-medium-light">
                            <ul>
                                <li
                                    v-for="item in navigationItems"
                                    :key="item.text"
                                >
                                    <nuxt-link
                                        :to="localePath(item.links[0])"
                                        class="nav-item cursor-pointer flex gap-x-3 items-center uppercase py-1.75 pl-4 border-l-[3px] border-transparent font-normal tracking-[0.125rem]"
                                        :class="{ 'nav-item-active': isActive(item) }"
                                        @click="closeOnMobile"
                                    >
                                        {{ $t(item.text) }}

                                        <account-sidebar-updated
                                            v-if="item.new"
                                            class="h-4 w-auto"
                                        />
                                    </nuxt-link>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

            <div v-if="user?.special_events?.christmas">
                <img
                    src="/images/sidebar/christmas.png"
                    :alt="$t('sidebar.christmas_tree')"
                    class="w-full h-auto mt-10 lg:mt-0 mb-20"
                />
            </div>
        </aside>
    </div>
</template>

<script setup lang="ts">
import type { KadeweUser, Store } from "~/types";
import IconInfo from "~/public/icons/info.svg";
import IconInfoBlack from "~/public/icons/info_black.svg";

const user = useSanctumUser<KadeweUser>();
const localePath = useLocalePath();
const sidebarStore = useSidebarStore();
const route = useRoute();
const sidebar = ref<HTMLDivElement | null>(null);
const { forceOpen } = storeToRefs(sidebarStore);
const isLoaded = ref(false);
const i18nLocale = useI18n();
const { t } = useI18n();
const dayjs = useDayjs();
const { isNewVisit } = useNavigationVisits();

interface NavigationItem {
    text: string;
    links: string[];
    visible: boolean;
    new: boolean;
}

const isActive = (item: NavigationItem): boolean => {
    for (const link of item.links) {
        if (route.path.startsWith(link)) {
            return true;
        }
    }

    return false;
};

const locale = computed<string>(() => {
    return i18nLocale.locale.value;
});

const memberSince = computed(() => {
    if (!user.value?.create_date) {
        return "/";
    }

    return new Date(user.value?.create_date ?? "").toLocaleDateString(locale.value);
});

const closeOnMobile = () => {
    sidebarStore.closeSidebar();
};

onMounted(() => {
    window.setTimeout(() => {
        isLoaded.value = true;
    }, 300);
});

const cardValidUntil = computed(() => {
    if (!user.value?.current_card_valid_until) {
        return "/";
    }

    return dayjs.utc(user.value.current_card_valid_until).format("MM.YYYY");
});

const showTicketShop = computed((): boolean => {
    const STORES_WITH_TICKETSHOP: Store[] = ["KADEWE", "ALSTERHAUS"];

    if (!user.value?.store) {
        return false;
    }

    return STORES_WITH_TICKETSHOP.includes(user.value.store);
});

const isFothUser = computed((): boolean => {
    return user.value?.foth.is_foth ?? false;
});

const sidebarTextColorClass = computed(() => {
    if (user.value?.foth.is_foth) {
        return "";
    }

    switch (user.value?.loyalty_status) {
        case "superior":
            return "text-color-superior";
        case "royal":
            return "text-color-royal";
    }

    return "";
});

const sidebarStatusLocalized = computed(() => {
    if (user.value?.foth.is_foth) {
        return t("sidebar.status_foth");
    }

    const status = (user.value?.loyalty_status ?? "member").toLowerCase();

    if (status === "member") {
        return t("sidebar.status_classic");
    }

    return t("sidebar.status_" + status);
});

const navigationItems = computed((): NavigationItem[] => {
    return [
        { text: "sidebar.portal", links: ["/portal/card"], visible: true, new: isNewVisit("loyalty_card") },
        {
            text: "sidebar.status",
            links: ["/portal/status"],
            visible: !isFothUser.value,
            new: isNewVisit("transactions") || isNewVisit("loyalty_points"),
        },
        {
            text: "sidebar.purchases",
            links: ["/portal/purchases"],
            visible: isFothUser.value,
            new: isNewVisit("transactions"),
        },
        {
            text: "sidebar.rewards",
            links: ["/portal/rewards", "/portal/coupons", "/portal/parking", "/portal/benefits"],
            visible: true,
            new: isNewVisit("rewards"),
        },
        {
            text: "sidebar.advent-calendar",
            links: ["/portal/advent-calendar"],
            visible: user.value?.special_events.advent_calendar ?? false,
            new: false,
        },
        { text: "sidebar.events", links: ["/portal/promotions"], visible: true, new: isNewVisit("events") },
        {
            text: "sidebar.ticketshop",
            links: ["/portal/ticketshop"],
            visible: showTicketShop.value,
            new: isNewVisit("ticketshop"),
        },
        {
            text: "sidebar.account",
            links: ["/portal/account", "/portal/notifications"],
            visible: true,
            new: isNewVisit("account"),
        },
        { text: "sidebar.help", links: ["/portal/help"], visible: true, new: isNewVisit("help") },
        { text: "sidebar.logout", links: ["/portal/logout"], visible: true, new: false },
    ].filter((item) => item.visible);
});
</script>

<style scoped>
@reference "../../assets/css/app.css";

.nav-item-active {
    @apply font-bold tracking-[0.0156rem];

    border-image: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0) 25%,
            rgba(255, 255, 255, 1) 25%,
            rgba(255, 255, 255, 1) 75%,
            rgba(255, 255, 255, 0) 75%,
            rgba(255, 255, 255, 0) 100%
        )
        0 100%;
}

@media (hover: hover) {
    .nav-item:hover {
        @apply font-medium tracking-wide;
        border-image: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0) 0%,
                rgba(255, 255, 255, 0) 25%,
                rgba(255, 255, 255, 1) 25%,
                rgba(255, 255, 255, 1) 75%,
                rgba(255, 255, 255, 0) 75%,
                rgba(255, 255, 255, 0) 100%
            )
            0 100%;
    }
}

.sidebar-force-open {
    transform: translateX(-100%);
    animation: slide-in-left 0.3s forwards;
}

@keyframes slide-in-left {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(0);
    }
}

.sidebar-force-close {
    transform: translateX(0);
    animation: slide-out-left 0.3s forwards;
}

.sidebar-force-close.sidebar-loading {
    animation-duration: 0s;
}

.sidebar-loading {
    visibility: hidden;
}

@media (width >= theme(--breakpoint-lg)) {
    .sidebar-force-open,
    .sidebar-force-close {
        transform: translateX(0);
        animation: none;
    }
}

@keyframes slide-out-left {
    0% {
        transform: translateX(0);
    }

    100% {
        transform: translateX(-100%);
    }
}

.text-color-superior {
    background: linear-gradient(
        281.38deg,
        rgba(162, 162, 162, 0.8) 5.52%,
        rgba(241, 243, 243, 0.8) 47.66%,
        rgba(223, 223, 223, 0.8) 88.7%
    );
    /** Move apply statement to override bg clip text */
    @apply bg-clip-text;
    -webkit-text-fill-color: transparent;
}

.text-color-royal {
    background:
        linear-gradient(0deg, #eaada0, #eaada0),
        linear-gradient(278.52deg, #b88172 -36.7%, #f9d0c2 48.45%, #eaada0 102.94%);
    @apply bg-clip-text;
    -webkit-text-fill-color: transparent;
}

:deep(.tooltip-arrow) {
    /** Hack because the sidebar "i" icon is relatively positioned at a slightly different position */
    transform: translate(-7px, 6px);
}
</style>
